/**
 * Utility for deferring non-critical script initialization
 * Uses requestIdleCallback when available, falls back to setTimeout
 */

/**
 * Defer a function call until the browser is idle or after a delay
 * @param callback - Function to call when idle
 * @param timeout - Maximum time to wait in ms (default: 3000ms)
 */
export function deferUntilIdle(callback: () => void, timeout: number = 3000): void {
  if (typeof window === "undefined") {
    // SSR: execute immediately
    callback();
    return;
  }

  // Use requestIdleCallback if available (modern browsers)
  if ("requestIdleCallback" in window) {
    const id = window.requestIdleCallback(
      () => {
        callback();
      },
      { timeout }
    );

    // Fallback to setTimeout if requestIdleCallback times out
    setTimeout(() => {
      try {
        window.cancelIdleCallback(id);
      } catch {
        // Ignore if already called
      }
      callback();
    }, timeout);
  } else {
    // Fallback for browsers without requestIdleCallback
    // Wait for DOMContentLoaded, then defer
    if (document.readyState === "loading") {
      document.addEventListener("DOMContentLoaded", () => {
        setTimeout(callback, 100);
      });
    } else {
      // DOM already loaded, defer with a short delay
      setTimeout(callback, 100);
    }
  }
}

/**
 * Defer a function call until after initial page load
 * Waits for load event, then defers further
 * @param callback - Function to call after load
 * @param delay - Additional delay after load in ms (default: 2000ms)
 */
export function deferAfterLoad(callback: () => void, delay: number = 2000): void {
  if (typeof window === "undefined") {
    callback();
    return;
  }

  if (document.readyState === "complete") {
    // Page already loaded, defer immediately
    deferUntilIdle(callback, delay);
  } else {
    // Wait for page load, then defer
    window.addEventListener(
      "load",
      () => {
        deferUntilIdle(callback, delay);
      },
      { once: true }
    );
  }
}
